import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import {
  AllI18nFamiliesQuery,
  MetricsSortField,
  SortDirection,
  useGetDashboardBreakdownQuery,
  useGetDashboardSummaryQuery,
  useGetUserFamiliesQuery,
  useGetUserPackagesQuery,
} from 'src/generated/graphql';
import { useQueryResult } from 'src/components/common/provider/QueryResultProvider';
import { SuperuserContext } from 'src/components/auth/SuperuserContext';
import DashboardFilters from './DashboardFilters';
import DashboardSummaryCards from './DashboardSummaryCards';
import DashboardBreakdownTable, { SortState } from './DashboardBreakdownTable';
import { DateRange } from './types';
import { computeDefaultDateRange } from './dateUtils';
import style from './DashboardView.module.scss';

type Props = Record<string, never>;

// Coral expects comma-separated strings (or omitted) — not arrays.
// Empty arrays become `undefined` so the field is omitted entirely.
const arrayToCsv = (arr: string[]): string | undefined =>
  arr.length > 0 ? arr.join(',') : undefined;

// Default page size for the breakdown table. Matches Coral's default `limit`
// for getMetricsBreakdown (20).
const BREAKDOWN_PAGE_SIZE = 20;

// Default sort matches the backend's documented default
// (sortField=totalRequests, sortDirection=DESCENDING).
const DEFAULT_SORT: SortState = {
  field: MetricsSortField.TotalRequests,
  direction: SortDirection.D,
};

// Sort families case-insensitively. The AllI18nFamilies endpoint returns
// the canonical list unsorted; alphabetical order is friendlier for a
// dropdown. The userFamilies path comes back already pre-ordered upstream
// so this only matters for the sudo fall-through.
const sortFamilies = (names: string[]): string[] =>
  [...names].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );

const DashboardView: React.FC<Props> = () => {
  const { t } = useTranslation();

  // Global sudo state — toggled via the user avatar in the header. The
  // dashboard does not own this state; it just reads it and threads it
  // through to the backend queries. Same pattern as StringList and
  // TargetSearch.
  const { isSudoMode, isSuperUserLoading } = useContext(SuperuserContext);

  const [dateRange, setDateRange] = useState<DateRange>(
    computeDefaultDateRange
  );
  const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);
  const [selectedPackageKeys, setSelectedPackageKeys] = useState<string[]>([]);
  const [breakdownSort, setBreakdownSort] = useState<SortState>(DEFAULT_SORT);
  const [breakdownPage, setBreakdownPage] = useState<number>(1);

  // Whenever filters or sort change, reset breakdown pagination to page 1
  // synchronously inside the same handler.
  const handleDateRangeChange = (next: DateRange) => {
    setDateRange(next);
    setBreakdownPage(1);
  };
  const handleFamiliesChange = (families: string[]) => {
    setSelectedFamilies(families);
    setBreakdownPage(1);
  };
  const handlePackageKeysChange = (keys: string[]) => {
    setSelectedPackageKeys(keys);
    setBreakdownPage(1);
  };
  const handleBreakdownSortChange = (next: SortState) => {
    setBreakdownSort(next);
    setBreakdownPage(1);
  };

  // Filter dropdown population. The `sudo` flag is sourced from the global
  // SuperuserContext — defaults to false, flipped by clicking the avatar.
  // We skip until superuser status is resolved so we don't fire twice on
  // initial mount with `sudo: false` then `sudo: true`.
  const familiesQuery = useGetUserFamiliesQuery({
    variables: { params: { sudo: isSudoMode } },
    skip: isSuperUserLoading,
  });
  const packagesQuery = useGetUserPackagesQuery({
    variables: { params: { sudo: isSudoMode } },
    skip: isSuperUserLoading,
  });

  // All-families lazy fetcher from the surrounding I18nFamilyQueryResultProvider.
  // Only fired in sudo mode — when the backend returns an empty userFamilies
  // list as the "all access" wire signal, we source the dropdown from the
  // canonical system-wide list. The Provider's context type is generic over
  // `any`; we narrow `data` here to the actual schema type.
  const {
    data: allFamiliesData,
    loading: allFamiliesLoading,
    fetch: fetchAllFamilies,
  } = useQueryResult();
  const allFamiliesTyped = allFamiliesData as AllI18nFamiliesQuery | undefined;

  // Trigger the all-families lazy query exactly once sudo mode is enabled.
  // The lazy query's fetch reference is stable across renders, so this
  // effect doesn't loop.
  useEffect(() => {
    if (isSudoMode) {
      fetchAllFamilies();
    }
  }, [isSudoMode, fetchAllFamilies]);

  // Clear filter selections + reset pagination when sudo mode flips.
  //
  // When sudo turns off, any families/packages picked from the sudo-only
  // canonical lists are no longer in the caller's permitted set; sending
  // them to the backend would either error or silently return empty
  // results. When sudo turns on, the available options widen — keeping a
  // stale selection is also surprising. Clearing in both directions is
  // the predictable behavior and matches how StringList/TargetSearch
  // handle sudo transitions in this codebase.
  //
  // Skip the initial mount — selections start as [] and page as 1, so
  // there's nothing to clear and firing setState here only adds a render
  // pass with no behavioral effect.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setSelectedFamilies([]);
    setSelectedPackageKeys([]);
    setBreakdownPage(1);
  }, [isSudoMode]);

  // Resolve the active family list:
  //   - sudo mode: all i18n families from the system, sorted.
  //   - otherwise: the caller's permitted families.
  const availableFamilies = useMemo<string[]>(() => {
    if (isSudoMode) {
      const names = (allFamiliesTyped?.i18nFamilies ?? [])
        .map((f) => f?.name)
        .filter((n): n is string => !!n);
      return sortFamilies(names);
    }
    return familiesQuery.data?.userFamilies?.i18nFamilies ?? [];
  }, [isSudoMode, allFamiliesTyped, familiesQuery.data]);

  const familiesLoading = isSudoMode
    ? familiesQuery.loading || allFamiliesLoading
    : familiesQuery.loading;

  // Filter params shared between summary and breakdown queries. The backend
  // Coral contract requires `sudo` on both metrics ops.
  const filterParams = useMemo(
    () => ({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      i18nFamilies: arrayToCsv(selectedFamilies),
      packageKeys: arrayToCsv(selectedPackageKeys),
      sudo: isSudoMode,
    }),
    [dateRange, selectedFamilies, selectedPackageKeys, isSudoMode]
  );

  // Summary metrics — re-fires automatically when any filter variable changes.
  const summaryQuery = useGetDashboardSummaryQuery({
    variables: { params: filterParams },
    skip: isSuperUserLoading,
  });

  // Breakdown metrics — re-fires when filters, sort, or pagination change.
  const breakdownQuery = useGetDashboardBreakdownQuery({
    variables: {
      params: {
        ...filterParams,
        sortField: breakdownSort.field,
        sortDirection: breakdownSort.direction,
        offset: (breakdownPage - 1) * BREAKDOWN_PAGE_SIZE,
        limit: BREAKDOWN_PAGE_SIZE,
      },
    },
    skip: isSuperUserLoading,
  });

  return (
    <div id="dashboard-view">
      <div className={style.stack}>
        <h1>{t('lui-dashboard-title')}</h1>

        <DashboardFilters
          dateRange={dateRange}
          selectedFamilies={selectedFamilies}
          selectedPackageKeys={selectedPackageKeys}
          availableFamilies={availableFamilies}
          availablePackages={
            packagesQuery.data?.userPackages?.packageKeys ?? []
          }
          familiesLoading={familiesLoading}
          packagesLoading={packagesQuery.loading}
          onDateRangeChange={handleDateRangeChange}
          onFamiliesChange={handleFamiliesChange}
          onPackageKeysChange={handlePackageKeysChange}
        />

        <DashboardSummaryCards
          loading={summaryQuery.loading}
          error={summaryQuery.error}
          data={summaryQuery.data?.dashboardSummary}
          onRetry={() => summaryQuery.refetch()}
        />

        <DashboardBreakdownTable
          loading={breakdownQuery.loading}
          error={breakdownQuery.error}
          data={breakdownQuery.data?.dashboardBreakdown}
          sort={breakdownSort}
          page={breakdownPage}
          pageSize={BREAKDOWN_PAGE_SIZE}
          onSortChange={handleBreakdownSortChange}
          onPageChange={setBreakdownPage}
          onRetry={() => breakdownQuery.refetch()}
        />
      </div>
    </div>
  );
};

export default DashboardView;
